import {
  Box,
  Card,
  CardBody,
  HStack,
  VStack,
  IconButton,
  Tooltip,
  Badge,
  Text,
  Divider,
  Switch,
  FormControl,
  FormLabel,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Heading,
  useToast,
  Portal,
  Textarea,
} from '@chakra-ui/react'
import {
  MdZoomIn,
  MdZoomOut,
  MdCenterFocusStrong,
  MdFullscreen,
  MdNavigateBefore,
  MdNavigateNext,
  MdVisibility,
  MdVisibilityOff,
  MdCheckCircle,
  MdCancel,
  MdScience,
  MdComment,
  MdAdd,
  MdRadioButtonUnchecked,
} from 'react-icons/md'
import { useState, useRef, useCallback, useMemo } from 'react'
import { IDiagnosticImage } from '../../../types/diagnostic-image'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useDirectMalariaAnalysis } from '../../../api/api_hooks/analysisMalaria/direct'
import { useGetAllImageZones } from '../../../api/api_hooks/image-zones'
import { useCreateImageZone } from '../../../api/api_hooks/comments'
import AnnotationThread from './AnnotationThread'
import { getSavedAuthData } from '../../../api/axios/interceptor'

interface WhiteBoardImageViewerProps {
  diagnosticImages: IDiagnosticImage[]
  refetchDiagnosticImages: () => void
}

const WhiteBoardImageViewer = ({
  diagnosticImages,
  refetchDiagnosticImages,
}: WhiteBoardImageViewerProps) => {
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [zoom, setZoom] = useState(1.0)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [showAnnotations, setShowAnnotations] = useState(true)
  const [showPositiveOnly, setShowPositiveOnly] = useState(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { mutateAsync: runMalariaAnalysis, status: malariaStatus } =
    useDirectMalariaAnalysis()
  const isAnalyzing = malariaStatus === 'pending'

  // Annotation states
  const [isAnnotationMode, setIsAnnotationMode] = useState(false)
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null)
  const [resolvedZones, setResolvedZones] = useState<Set<string>>(new Set())
  const [isDrawing, setIsDrawing] = useState(false)
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null)
  const [drawEnd, setDrawEnd] = useState<{ x: number; y: number } | null>(null)
  const [pendingAnnotation, setPendingAnnotation] = useState<{
    coordinates: number[]
    bounds: { left: number; top: number; width: number; height: number }
  } | null>(null)
  const [pendingCommentText, setPendingCommentText] = useState('')
  const [isCreatingAnnotation, setIsCreatingAnnotation] = useState(false)

  const imageRef = useRef<HTMLImageElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const currentImage = diagnosticImages[currentIndex]

  // Get image zones for current image
  const { result: imageZones = [], refetch: refetchImageZones } = useGetAllImageZones(
    currentImage?.diagnosticId || '',
    currentImage?.id || ''
  )
  const { mutateAsync: createImageZone } = useCreateImageZone()
  const hasAnalysis = currentImage?.coordinates &&
    (currentImage.coordinates.positive.length > 0 || currentImage.coordinates.negative.length > 0)

  // Parse bounding boxes
  const positiveBoxes = useMemo(() =>
    (currentImage?.coordinates?.positive ?? []).filter((coords: any): coords is [number, number, number, number] =>
      Array.isArray(coords) && coords.length === 4
    ), [currentImage?.coordinates?.positive]
  )

  const negativeBoxes = useMemo(() =>
    (currentImage?.coordinates?.negative ?? []).filter((coords: any): coords is [number, number, number, number] =>
      Array.isArray(coords) && coords.length === 4
    ), [currentImage?.coordinates?.negative]
  )

  const totalDetections = positiveBoxes.length + negativeBoxes.length
  const positiveRate = totalDetections > 0
    ? (positiveBoxes.length / totalDetections) * 100
    : 0

  // Reset view
  const resetView = useCallback(() => {
    setZoom(1.0)
    setPan({ x: 0, y: 0 })
  }, [])

  // Navigation
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : diagnosticImages.length - 1))
    resetView()
  }, [diagnosticImages.length, resetView])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < diagnosticImages.length - 1 ? prev + 1 : 0))
    resetView()
  }, [diagnosticImages.length, resetView])

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + 0.25, 5.0))
  }, [])

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - 0.25, 0.5))
  }, [])

  // Pan handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true)
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
    }
  }, [zoom, pan])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      })
    }
  }, [isDragging, dragStart, zoom])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.1 : 0.1
    setZoom((prev) => Math.max(0.5, Math.min(5.0, prev + delta)))
  }, [])

  // Annotation handlers
  const handleToggleAnnotationMode = useCallback(() => {
    setIsAnnotationMode((prev) => !prev)
    // Don't open sidebar immediately - let user draw the region first
  }, [])

  const handleAnnotationMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isAnnotationMode || !imageRef.current) return

    const rect = imageRef.current.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100

    setIsDrawing(true)
    setDrawStart({ x, y })
    setDrawEnd({ x, y })
  }, [isAnnotationMode])

  const handleAnnotationMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDrawing || !drawStart || !imageRef.current) return

    const rect = imageRef.current.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100

    setDrawEnd({ x, y })
  }, [isDrawing, drawStart])

  const handleAnnotationMouseUp = useCallback(() => {
    if (!isDrawing || !drawStart || !drawEnd || !currentImage) return

    const width = Math.abs(drawEnd.x - drawStart.x)
    const height = Math.abs(drawEnd.y - drawStart.y)

    // Minimum size check (at least 2% of image)
    if (width < 2 || height < 2) {
      setIsDrawing(false)
      setDrawStart(null)
      setDrawEnd(null)
      return
    }

    const left = Math.min(drawStart.x, drawEnd.x)
    const top = Math.min(drawStart.y, drawEnd.y)
    const right = left + width
    const bottom = top + height

    // Convert percentage coordinates to pixel coordinates
    // Backend expects pixels relative to original image dimensions
    const leftPx = Math.floor((left / 100) * currentImage.coordinates.width)
    const topPx = Math.floor((top / 100) * currentImage.coordinates.height)
    const rightPx = Math.floor((right / 100) * currentImage.coordinates.width)
    const bottomPx = Math.floor((bottom / 100) * currentImage.coordinates.height)

    // Store pending annotation instead of creating it immediately
    setPendingAnnotation({
      coordinates: [leftPx, topPx, rightPx, bottomPx],
      bounds: { left, top, width, height },
    })

    setIsDrawing(false)
    setDrawStart(null)
    setDrawEnd(null)
  }, [isDrawing, drawStart, drawEnd, currentImage])

  const handleSubmitPendingAnnotation = useCallback(async () => {
    if (!pendingAnnotation || !currentImage || !pendingCommentText.trim()) return

    setIsCreatingAnnotation(true)
    try {
      const response = await createImageZone({
        diagnosticImageId: currentImage.id,
        coordinates: pendingAnnotation.coordinates,
        commentText: pendingCommentText.trim(),
      })

      // Get the newly created zone ID from the response
      const newZoneId = response.data?.dataValues?.id || response.data?.id

      // Refetch zones to get the updated list
      await refetchImageZones()

      setIsAnnotationMode(false) // Exit annotation mode after successful creation
      setPendingAnnotation(null)
      setPendingCommentText('')

      // Small delay to ensure the refetch is complete and UI is updated
      setTimeout(() => {
        if (newZoneId) {
          setSelectedZoneId(newZoneId)
        }

        toast({
          title: t('whiteboard.annotations.annotationCreated'),
          status: 'success',
          duration: 2000,
          isClosable: true,
        })
      }, 100)
    } catch (error) {
      toast({
        title: t('shared.errors.errorMsg'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsCreatingAnnotation(false)
    }
  }, [pendingAnnotation, pendingCommentText, currentImage, createImageZone, refetchImageZones, toast, t])

  const handleCancelPendingAnnotation = useCallback(() => {
    setPendingAnnotation(null)
    setPendingCommentText('')
  }, [])

  const handleToggleResolved = useCallback((zoneId: string, resolved: boolean) => {
    setResolvedZones((prev) => {
      const newSet = new Set(prev)
      if (resolved) {
        newSet.add(zoneId)
      } else {
        newSet.delete(zoneId)
      }
      return newSet
    })
  }, [])

  const handleZoneDeleted = useCallback(() => {
    // Close the modal
    setSelectedZoneId(null)

    // Update cache directly to remove the deleted zone
    if (currentImage?.diagnosticId && currentImage?.id) {
      const { userId } = getSavedAuthData()
      const imageZonesQueryKey = `diagnostics/${currentImage.diagnosticId}/diagnostic-images/${currentImage.id}/image-zones${userId}`

      // Get current zones and remove the deleted one
      const currentZones = queryClient.getQueryData<typeof imageZones>([imageZonesQueryKey])
      if (currentZones && selectedZoneId) {
        const updatedZones = currentZones.filter((z: any) => z.id !== selectedZoneId)
        queryClient.setQueryData([imageZonesQueryKey], updatedZones)
      }
    }
  }, [queryClient, currentImage, selectedZoneId])

  // Render annotation zones with numbered badges
  const renderAnnotationZones = useCallback(() => {
    if (!imageRef.current || !currentImage) return null

    const imgEl = imageRef.current
    const renderedWidth = imgEl.clientWidth
    const renderedHeight = imgEl.clientHeight

    return imageZones.map((zone, index) => {
      const isSelected = selectedZoneId === zone.id
      const isResolved = resolvedZones.has(zone.id)

      // Zone coordinates are in pixels relative to original image dimensions
      // Scale them to the rendered image size
      const scaleX = renderedWidth / currentImage.coordinates.width
      const scaleY = renderedHeight / currentImage.coordinates.height

      const pixelLeft = zone.left * scaleX
      const pixelTop = zone.top * scaleY
      const pixelWidth = zone.width * scaleX
      const pixelHeight = zone.height * scaleY

      return (
        <Box key={zone.id}>
          {/* Annotation rectangle */}
          <Box
            position="absolute"
            left={`${pixelLeft}px`}
            top={`${pixelTop}px`}
            width={`${pixelWidth}px`}
            height={`${pixelHeight}px`}
            border="3px solid"
            borderColor={isSelected ? 'purple.500' : isResolved ? 'green.400' : 'purple.400'}
            bg={isSelected ? 'purple.100' : 'transparent'}
            opacity={isSelected ? 0.4 : 0.3}
            pointerEvents="none"
            borderRadius="md"
            transition="all 0.2s"
          />

          {/* Numbered badge */}
          <Box
            position="absolute"
            left={`${pixelLeft}px`}
            top={`${pixelTop - 16}px`}
            w="32px"
            h="32px"
            borderRadius="full"
            bg={isResolved ? 'green.500' : 'purple.500'}
            color="white"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontWeight="bold"
            fontSize="sm"
            boxShadow="lg"
            cursor="pointer"
            onClick={() => {
              setSelectedZoneId(isSelected ? null : zone.id)
            }}
            _hover={{
              transform: 'scale(1.1)',
              boxShadow: 'xl',
            }}
            transition="all 0.2s"
            zIndex={10}
          >
            {index + 1}
          </Box>

          {/* Comment modal - rendered outside zoom with Portal */}
          {isSelected && (
            <Portal>
              <Box
                position="fixed"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                zIndex={1500}
              >
                <Box
                  w="500px"
                  maxW="95vw"
                  maxH="85vh"
                  bg="white"
                  boxShadow="2xl"
                  borderWidth="2px"
                  borderColor="purple.200"
                  borderRadius="lg"
                  display="flex"
                  flexDirection="column"
                >
                  {/* Header */}
                  <Box
                    p={4}
                    fontWeight="bold"
                    borderBottomWidth="1px"
                    bgGradient="linear(to-r, purple.50, pink.50)"
                    position="relative"
                  >
                    <IconButton
                      aria-label="close"
                      icon={<MdCancel />}
                      size="sm"
                      position="absolute"
                      right={2}
                      top={2}
                      onClick={() => setSelectedZoneId(null)}
                      variant="ghost"
                    />
                    <HStack spacing={2} justify="space-between" pr={8}>
                      <HStack spacing={2}>
                        <Badge colorScheme={isResolved ? 'green' : 'purple'} fontSize="md" px={3} py={1} borderRadius="full">
                          #{index + 1}
                        </Badge>
                        <Text fontSize="sm">
                          {zone.commentsCount || 0} {zone.commentsCount === 1 ? t('shared.common.comment') : t('shared.common.comments')}
                        </Text>
                      </HStack>
                      <Tooltip
                        label={
                          isResolved
                            ? t('whiteboard.annotations.markAsUnresolved')
                            : t('whiteboard.annotations.markAsResolved')
                        }
                      >
                        <IconButton
                          aria-label="toggle-resolved"
                          icon={isResolved ? <MdCheckCircle /> : <MdRadioButtonUnchecked />}
                          size="xs"
                          variant="ghost"
                          colorScheme={isResolved ? 'green' : 'purple'}
                          onClick={() => handleToggleResolved(zone.id, !isResolved)}
                        />
                      </Tooltip>
                    </HStack>
                  </Box>

                  {/* Body */}
                  <Box p={4} flex="1" overflowY="auto">
                    <AnnotationThread
                      imageZone={zone}
                      annotationNumber={index + 1}
                      isResolved={isResolved}
                      onResolve={handleToggleResolved}
                      onZoneDeleted={handleZoneDeleted}
                    />
                  </Box>
                </Box>
              </Box>
            </Portal>
          )}
        </Box>
      )
    })
  }, [imageZones, selectedZoneId, resolvedZones, currentImage, handleToggleResolved, handleZoneDeleted, t])

  // Render drawing rectangle
  const renderDrawingRect = useCallback(() => {
    if (!isDrawing || !drawStart || !drawEnd || !imageRef.current) return null

    const imgEl = imageRef.current
    const renderedWidth = imgEl.clientWidth
    const renderedHeight = imgEl.clientHeight

    const left = Math.min(drawStart.x, drawEnd.x)
    const top = Math.min(drawStart.y, drawEnd.y)
    const width = Math.abs(drawEnd.x - drawStart.x)
    const height = Math.abs(drawEnd.y - drawStart.y)

    const pixelLeft = (left * renderedWidth) / 100
    const pixelTop = (top * renderedHeight) / 100
    const pixelWidth = (width * renderedWidth) / 100
    const pixelHeight = (height * renderedHeight) / 100

    return (
      <Box
        position="absolute"
        left={`${pixelLeft}px`}
        top={`${pixelTop}px`}
        width={`${pixelWidth}px`}
        height={`${pixelHeight}px`}
        border="3px dashed"
        borderColor="purple.500"
        bg="purple.200"
        opacity={0.4}
        pointerEvents="none"
        borderRadius="md"
      />
    )
  }, [isDrawing, drawStart, drawEnd])

  // Render pending annotation with comment input
  const renderPendingAnnotation = useCallback(() => {
    if (!pendingAnnotation || !imageRef.current || !currentImage) return null

    const imgEl = imageRef.current
    const renderedWidth = imgEl.clientWidth
    const renderedHeight = imgEl.clientHeight

    const { left, top, width, height } = pendingAnnotation.bounds

    const pixelLeft = (left * renderedWidth) / 100
    const pixelTop = (top * renderedHeight) / 100
    const pixelWidth = (width * renderedWidth) / 100
    const pixelHeight = (height * renderedHeight) / 100

    return (
      <>
        {/* Pending annotation rectangle - inside zoomed container */}
        <Box
          key="pending-annotation-rect"
          position="absolute"
          left={`${pixelLeft}px`}
          top={`${pixelTop}px`}
          width={`${pixelWidth}px`}
          height={`${pixelHeight}px`}
          border="3px solid"
          borderColor="orange.500"
          bg="orange.100"
          opacity={0.5}
          pointerEvents="none"
          borderRadius="md"
          animation="pulse 2s infinite"
        />

        {/* Comment input modal - outside zoom with Portal */}
        <Portal>
          <Box
            position="fixed"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            zIndex={1500}
          >
            <Box
              w="450px"
              maxW="95vw"
              bg="white"
              boxShadow="2xl"
              borderWidth="2px"
              borderColor="orange.300"
              borderRadius="lg"
            >
              <Box
                p={4}
                fontWeight="bold"
                borderBottomWidth="1px"
                bgGradient="linear(to-r, orange.50, yellow.50)"
              >
                <HStack spacing={2}>
                  <MdComment />
                  <Text>{t('whiteboard.annotations.addFirstComment')}</Text>
                </HStack>
              </Box>

              <Box p={4}>
                <VStack spacing={3} align="stretch">
                  <Text fontSize="sm" color="gray.600">
                    {t('whiteboard.annotations.commentRequired')}
                  </Text>
                  <Textarea
                    value={pendingCommentText}
                    onChange={(e) => setPendingCommentText(e.target.value)}
                    placeholder={t('whiteboard.annotations.writeComment')}
                    size="sm"
                    rows={4}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        handleSubmitPendingAnnotation()
                      }
                      if (e.key === 'Escape') {
                        handleCancelPendingAnnotation()
                      }
                    }}
                  />
                </VStack>
              </Box>

              <Box p={4} borderTopWidth="1px">
                <HStack justify="flex-end" spacing={2}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCancelPendingAnnotation}
                  >
                    {t('shared.common.cancel')}
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="purple"
                    onClick={handleSubmitPendingAnnotation}
                    isLoading={isCreatingAnnotation}
                    isDisabled={!pendingCommentText.trim()}
                  >
                    {t('whiteboard.annotations.createAnnotation')}
                  </Button>
                </HStack>
              </Box>
            </Box>
          </Box>
        </Portal>
      </>
    )
  }, [
    pendingAnnotation,
    pendingCommentText,
    currentImage,
    isCreatingAnnotation,
    handleSubmitPendingAnnotation,
    handleCancelPendingAnnotation,
    t,
  ])

  // Render bounding boxes
  // Note: Bounding boxes already include padding (MALARIA_BBOX_PADDING = 4px) applied by backend
  // See: careflai-be/src/services/ai.js adjustBoundingBoxes() function
  const renderBoxes = useCallback(() => {
    if (!showAnnotations || !imageRef.current) return null

    const imgEl = imageRef.current
    const renderedWidth = imgEl.clientWidth
    const renderedHeight = imgEl.clientHeight
    const naturalWidth = imgEl.naturalWidth
    const naturalHeight = imgEl.naturalHeight

    if (!naturalWidth || !naturalHeight) return null

    const scaleX = renderedWidth / naturalWidth
    const scaleY = renderedHeight / naturalHeight

    const boxes = [
      ...positiveBoxes.map((coords: [number, number, number, number], idx: number) => ({ coords, type: 'positive' as const, idx })),
      ...(showPositiveOnly ? [] : negativeBoxes.map((coords: [number, number, number, number], idx: number) => ({ coords, type: 'negative' as const, idx }))),
    ]

    return boxes.map(({ coords, type, idx }) => {
      // Coordinates already include backend-applied padding
      const [x1, y1, x2, y2] = coords
      const pixelX = x1 * scaleX
      const pixelY = y1 * scaleY
      const pixelWidth = (x2 - x1) * scaleX
      const pixelHeight = (y2 - y1) * scaleY

      return (
        <Box
          key={`${type}-${idx}`}
          position="absolute"
          left={`${pixelX}px`}
          top={`${pixelY}px`}
          width={`${pixelWidth}px`}
          height={`${pixelHeight}px`}
          border="2px solid"
          borderColor={type === 'positive' ? 'red.400' : 'green.400'}
          bg={type === 'positive' ? 'rgba(255, 0, 0, 0.1)' : 'rgba(0, 255, 0, 0.1)'}
          borderRadius="sm"
          pointerEvents="none"
          transition="all 0.2s"
        />
      )
    })
  }, [showAnnotations, positiveBoxes, negativeBoxes, showPositiveOnly])

  if (diagnosticImages.length === 0) {
    return (
      <Card>
        <CardBody>
          <Text color="gray.500" textAlign="center">{t('whiteboard.noImages')}</Text>
        </CardBody>
      </Card>
    )
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Controls Toolbar */}
      <Card shadow="sm">
        <CardBody>
          <HStack justify="space-between" wrap="wrap" spacing={4}>
            {/* Navigation */}
            <HStack>
              <IconButton
                aria-label="Previous image"
                icon={<MdNavigateBefore />}
                onClick={goToPrevious}
                isDisabled={diagnosticImages.length <= 1}
                size="sm"
              />
              <Text fontSize="sm" fontWeight="medium">
                {currentIndex + 1} / {diagnosticImages.length}
              </Text>
              <IconButton
                aria-label="Next image"
                icon={<MdNavigateNext />}
                onClick={goToNext}
                isDisabled={diagnosticImages.length <= 1}
                size="sm"
              />
            </HStack>

            {/* Zoom Controls */}
            <HStack spacing={2}>
              <Tooltip label={t('microscope.zoomOut')}>
                <IconButton
                  aria-label="Zoom out"
                  icon={<MdZoomOut />}
                  onClick={handleZoomOut}
                  isDisabled={zoom <= 0.5}
                  size="sm"
                  variant="outline"
                />
              </Tooltip>

              <Box width="100px">
                <Slider
                  value={zoom}
                  min={0.5}
                  max={5.0}
                  step={0.1}
                  onChange={setZoom}
                >
                  <SliderTrack>
                    <SliderFilledTrack bg="purple.400" />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>
              </Box>

              <Text fontSize="xs" minW="45px">{(zoom * 100).toFixed(0)}%</Text>

              <Tooltip label={t('microscope.zoomIn')}>
                <IconButton
                  aria-label="Zoom in"
                  icon={<MdZoomIn />}
                  onClick={handleZoomIn}
                  isDisabled={zoom >= 5.0}
                  size="sm"
                  variant="outline"
                />
              </Tooltip>

              <Tooltip label={t('microscope.resetView')}>
                <IconButton
                  aria-label="Reset view"
                  icon={<MdCenterFocusStrong />}
                  onClick={resetView}
                  size="sm"
                  variant="outline"
                />
              </Tooltip>

              <Tooltip label={t('microscope.fullscreen')}>
                <IconButton
                  aria-label="Fullscreen"
                  icon={<MdFullscreen />}
                  onClick={onOpen}
                  size="sm"
                  colorScheme="purple"
                />
              </Tooltip>
            </HStack>

            {/* Annotation Controls */}
            <Tooltip label={t('whiteboard.annotations.annotationMode')}>
              <Button
                leftIcon={<MdAdd />}
                size="sm"
                colorScheme="purple"
                variant={isAnnotationMode ? 'solid' : 'outline'}
                onClick={handleToggleAnnotationMode}
                rightIcon={
                  imageZones.length > 0 ? (
                    <Badge colorScheme="purple" borderRadius="full">
                      {imageZones.length}
                    </Badge>
                  ) : undefined
                }
              >
                {isAnnotationMode ? t('whiteboard.annotations.exitAnnotationMode') : t('whiteboard.annotations.addAnnotation')}
              </Button>
            </Tooltip>

            {/* Filter Controls */}
            {hasAnalysis && (
              <HStack spacing={3}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="show-annotations" mb="0" fontSize="sm">
                    {showAnnotations ? <MdVisibility /> : <MdVisibilityOff />}
                  </FormLabel>
                  <Switch
                    id="show-annotations"
                    isChecked={showAnnotations}
                    onChange={(e) => setShowAnnotations(e.target.checked)}
                    colorScheme="purple"
                    size="sm"
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="positive-only" mb="0" fontSize="sm">
                    Infected only
                  </FormLabel>
                  <Switch
                    id="positive-only"
                    isChecked={showPositiveOnly}
                    onChange={(e) => setShowPositiveOnly(e.target.checked)}
                    colorScheme="red"
                    size="sm"
                  />
                </FormControl>
              </HStack>
            )}
          </HStack>
        </CardBody>
      </Card>

      {/* Main Image Viewer */}
      <Card shadow="md" overflow="hidden">
        <CardBody p={0}>
          <Box
            ref={containerRef}
            position="relative"
            w="100%"
            h="600px"
            bg="gray.900"
            overflow="hidden"
            cursor={
              isAnnotationMode
                ? 'crosshair'
                : zoom > 1
                  ? isDragging
                    ? 'grabbing'
                    : 'grab'
                  : 'default'
            }
            onMouseDown={isAnnotationMode ? handleAnnotationMouseDown : handleMouseDown}
            onMouseMove={isAnnotationMode ? handleAnnotationMouseMove : handleMouseMove}
            onMouseUp={isAnnotationMode ? handleAnnotationMouseUp : handleMouseUp}
            onMouseLeave={isAnnotationMode ? handleAnnotationMouseUp : handleMouseUp}
            onWheel={handleWheel}
          >
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform={`translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`}
              transition={isDragging ? 'none' : 'transform 0.2s'}
            >
              <Box position="relative">
                <img
                  ref={imageRef}
                  src={currentImage.mediaUrl}
                  alt={`Diagnostic ${currentIndex + 1}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '600px',
                    display: 'block',
                    userSelect: 'none',
                  }}
                  draggable={false}
                />
                {renderBoxes()}
                {renderAnnotationZones()}
                {renderDrawingRect()}
                {renderPendingAnnotation()}
              </Box>
            </Box>
          </Box>
        </CardBody>
      </Card>

      {/* Run malaria analysis on the current displayed image */}
      <HStack justify="flex-start">
        <Tooltip label="Analyser l'image affichée avec le module malaria">
          <Button
            size="sm"
            leftIcon={<MdScience />}
            colorScheme="purple"
            onClick={async () => {
              if (!currentImage?.mediaUrl) return
              try {
                const resp = await fetch(currentImage.mediaUrl)
                const blob = await resp.blob()
                const file = new File([blob], `diagnostic-${currentImage.id}.png`, {
                  type: blob.type || 'image/png',
                })
                await runMalariaAnalysis(file)
                toast({
                  title: 'Analyse malaria lancée',
                  description: 'Les résultats apparaîtront sous l’image une fois le traitement terminé.',
                  status: 'success',
                  duration: 4000,
                  isClosable: true,
                })
                refetchDiagnosticImages()
              } catch (error: any) {
                console.error('Malaria analysis failed', error)
                toast({
                  title: 'Échec de l’analyse malaria',
                  description: error?.message || 'Impossible de lancer l’analyse.',
                  status: 'error',
                  duration: 5000,
                  isClosable: true,
                })
              }
            }}
            isLoading={isAnalyzing}
            loadingText="Analyse..."
            variant="outline"
          >
            Lancer le module malaria
          </Button>
        </Tooltip>
      </HStack>

      {/* Analysis Summary & Legend */}
      {hasAnalysis && (
        <Card shadow="sm">
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Heading size="sm">Detection Summary</Heading>

              <HStack justify="space-between" wrap="wrap">
                <Stat>
                  <StatLabel fontSize="xs">Total Detections</StatLabel>
                  <StatNumber fontSize="2xl">{totalDetections}</StatNumber>
                </Stat>

                <Stat>
                  <StatLabel fontSize="xs">
                    <HStack><MdCheckCircle color="red" /><Text>Infected RBC</Text></HStack>
                  </StatLabel>
                  <StatNumber fontSize="2xl" color="red.500">{positiveBoxes.length}</StatNumber>
                </Stat>

                <Stat>
                  <StatLabel fontSize="xs">
                    <HStack><MdCancel color="green" /><Text>Healthy RBC</Text></HStack>
                  </StatLabel>
                  <StatNumber fontSize="2xl" color="green.500">{negativeBoxes.length}</StatNumber>
                </Stat>

                <Stat>
                  <StatLabel fontSize="xs">Positive Rate</StatLabel>
                  <StatNumber fontSize="2xl" color="purple.500">
                    {positiveRate.toFixed(1)}%
                  </StatNumber>
                </Stat>
              </HStack>

              <Divider />

              <Box>
                <Heading size="xs" mb={2}>Legend</Heading>
                <HStack spacing={4}>
                  <HStack>
                    <Box w="20px" h="20px" border="2px solid" borderColor="red.400" bg="rgba(255,0,0,0.1)" />
                    <Text fontSize="sm">Infected RBC</Text>
                  </HStack>
                  <HStack>
                    <Box w="20px" h="20px" border="2px solid" borderColor="green.400" bg="rgba(0,255,0,0.1)" />
                    <Text fontSize="sm">Healthy RBC</Text>
                  </HStack>
                </HStack>
              </Box>
            </VStack>
          </CardBody>
        </Card>
      )}

      {/* Fullscreen Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay />
        <ModalContent bg="gray.900">
          <ModalCloseButton color="white" />
          <ModalBody p={0}>
            <Box
              w="100vw"
              h="100vh"
              position="relative"
              overflow="hidden"
              cursor={
                isAnnotationMode
                  ? 'crosshair'
                  : zoom > 1
                    ? isDragging
                      ? 'grabbing'
                      : 'grab'
                    : 'default'
              }
              onMouseDown={isAnnotationMode ? handleAnnotationMouseDown : handleMouseDown}
              onMouseMove={isAnnotationMode ? handleAnnotationMouseMove : handleMouseMove}
              onMouseUp={isAnnotationMode ? handleAnnotationMouseUp : handleMouseUp}
              onMouseLeave={isAnnotationMode ? handleAnnotationMouseUp : handleMouseUp}
              onWheel={handleWheel}
            >
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform={`translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) scale(${zoom})`}
                transition={isDragging ? 'none' : 'transform 0.2s'}
              >
                <Box position="relative">
                  <img
                    src={currentImage.mediaUrl}
                    alt={`Diagnostic ${currentIndex + 1}`}
                    style={{
                      maxWidth: '95vw',
                      maxHeight: '95vh',
                      display: 'block',
                      userSelect: 'none',
                    }}
                    draggable={false}
                  />
                  {renderBoxes()}
                  {renderAnnotationZones()}
                  {renderDrawingRect()}
                  {renderPendingAnnotation()}
                </Box>
              </Box>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  )
}

export default WhiteBoardImageViewer
